import React, { FC, Fragment, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { isEmpty, partition, includes, first } from 'lodash';
import DiamondSvg from '@shared/assets/svgs/diamond.svg';
import EuroSvg from '@shared/assets/svgs/euro.svg';
import { CandidateProfileDataItem } from '@src/components/CandidateProfileDataItem';
import FrBinocularSvg from '@src/svgs/fr-binocular.svg';
import FrUserSvg from '@src/svgs/fr-user.svg';
import FrClipboardSvg from '@src/svgs/fr-clipboard.svg';
import { useGetSalaryPeriodsQuery } from '@src/graphql/generated';
import { CandidateProfileDataItemsWithUserProps } from './props';
import '../styles.scss';

export const CandidateProfileDataItemsWithUser: FC<CandidateProfileDataItemsWithUserProps> = ({ className, user }) => {
  const { data: salaryPeriodsData } = useGetSalaryPeriodsQuery();

  const salaryPeriods = salaryPeriodsData?.salaryPeriods;

  const seniorities = user.profile.seniorities;
  const profileSkills = user.profile.skills;
  const profileSpeciality = user.profile.speciality;
  const profileSalary = user.profile.originalSalary;

  const mapNames = useCallback((list: { name: string }[]) => {
    return list.map((item, index) => {
      if (item.name?.length) {
        return (
          <Fragment key={item.name}>
            {index !== 0 && ' or '}

            <strong>{item.name}</strong>
          </Fragment>
        );
      }

      return null;
    });
  }, []);

  const profileList = useMemo(
    () => ({
      haveFollowingSkills: 'And have following skills',
      isLookingFor: 'Is looking for',
      monthlySalaryIs: 'Expected monthly salary is',
      seniorityIs: 'Who’s seniority is',
      specialityAre: 'Who’s speciality are',
    }),
    [],
  );

  const specialityTag = first(profileSpeciality.skillTags);

  const salaryPeriodLabel = salaryPeriods?.find(({ value }) => value === profileSalary.period)?.label;

  const [specialities, skills] = partition(profileSkills, ({ tags }) => includes(tags, specialityTag));

  return (
    <div className={classnames('candidate-profile-data-items', className)}>
      <CandidateProfileDataItem
        data={
          <Fragment>
            <strong>{profileSpeciality.name}</strong> positions
          </Fragment>
        }
        icon={FrBinocularSvg}
        label={profileList.isLookingFor}
      />

      {!isEmpty(specialities) && (
        <CandidateProfileDataItem
          className="candidate-profile-data-items__item"
          data={mapNames(specialities)}
          icon={FrUserSvg}
          label={profileList.specialityAre}
        />
      )}

      {!isEmpty(skills) && (
        <CandidateProfileDataItem
          className="candidate-profile-data-items__item"
          data={mapNames(skills)}
          icon={DiamondSvg}
          label={profileList.haveFollowingSkills}
        />
      )}

      {!isEmpty(seniorities) && (
        <CandidateProfileDataItem
          className="candidate-profile-data-items__item"
          data={mapNames(seniorities)}
          icon={FrClipboardSvg}
          label={profileList.seniorityIs}
        />
      )}

      <CandidateProfileDataItem
        className="candidate-profile-data-items__item"
        data={
          <strong>
            {' '}
            {profileSalary.currency.symbol}
            {profileSalary.value}
          </strong>
        }
        icon={EuroSvg}
        label={`Expected ${salaryPeriodLabel} salary is`}
      />
    </div>
  );
};
